import { FaCalendarAlt, FaUsers, FaExternalLinkAlt, FaTerminal, FaKeyboard, FaGithub, FaBook, FaCode, FaMicrophone, FaYoutube } from "react-icons/fa";
import Emoji from "../../ui/Emoji/Emoji";
import { ContentColumn } from "../../layout/ContentColumn/ContentColumn";

interface Business {
  id: string;
  name: string;
  tagline: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  // Optional, and deliberately so: only Coding has a mark and a recording today.
  // A venture without either falls back to the icon and to text, as before.
  logo?: string;
  video?: { src: string; poster: string; label: string };
  features: string[];
  cta: {
    primary: {
      text: string;
      url: string;
      icon: React.ComponentType<{ className?: string }>;
    };
    secondary?: {
      text: string;
      url: string;
      icon: React.ComponentType<{ className?: string }>;
    };
  };
  disclaimer?: string;
  stats?: Array<{
    value: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
  }>;
}

const BUSINESSES: Business[] = [
  {
    id: "clubcp",
    name: "CLUBCP",
    tagline: "Software for community clubs",
    description: "Free club-management software for volunteer-run community clubs in Australia. Memberships, events, ticketing, payments, posts and galleries in one place, so a committee spends its evenings running the club instead of reconciling a spreadsheet. It has been in production since September 2021.",
    icon: FaUsers,
    features: ["Memberships", "Events & Ticketing", "Payments"],
    stats: [
      {
        value: "1,600+",
        label: "Members",
        icon: FaUsers,
      },
      {
        value: "2021",
        label: "Live since",
        icon: FaCalendarAlt,
      },
    ],
    cta: {
      primary: {
        text: "Visit ClubCP",
        url: "https://clubcp.app",
        icon: FaExternalLinkAlt,
      },
    },
    disclaimer: "Free for clubs \u2022 Australia \u2022 Running continuously since 2021",
  },
  {
    id: "coding-drills",
    name: "CODING",
    tagline: "coding.gazar.dev",
    description: "AI writes the code now, and the part that fades is producing it from an empty file. So: ten minutes and one TypeScript problem, written from memory against hidden tests inside a countdown, and passing schedules it further out. Paste is blocked on purpose, because recognising code when you read it is a different skill from producing it. Not a LeetCode replacement, and not trying to be.",
    icon: FaKeyboard,
    logo: "/coding/mark.svg",
    video: {
      // Same-origin copy: see the note in routes/home.tsx.
      src: "/coding/demo.mp4",
      poster: "/coding/demo-poster.jpg",
      label: "Coding: a drill being solved, and why it exists",
    },
    features: ["TypeScript", "Hidden tests", "Spaced repetition"],
    cta: {
      primary: {
        text: "Start a drill",
        url: "https://coding.gazar.dev",
        icon: FaCode,
      },
      secondary: {
        text: "How it works",
        url: "https://coding.gazar.dev/how-it-works",
        icon: FaBook,
      },
    },
    disclaimer: "Free \u2022 No account needed to try one \u2022 15 problems, TypeScript only",
  },
  {
    id: "marlin",
    name: "MARLIN",
    tagline: "Open source terminal",
    description: "The terminal where you read what your agents did. Splits that nest, a file tree carrying real git status, staging and conflicts, and unified or side-by-side diffs, all built in, so reviewing what an agent changed does not mean leaving the terminal for an editor. Native, open source under MIT, no account and no telemetry.",
    icon: FaTerminal,
    features: ["Rust", "MIT licensed", "No account, no telemetry"],
    cta: {
      primary: {
        text: "Visit Marlin",
        url: "https://marlin.gazar.dev",
        icon: FaExternalLinkAlt,
      },
      secondary: {
        text: "Source on GitHub",
        url: "https://github.com/ehsangazar/marlin",
        icon: FaGithub,
      },
    },
    disclaimer: "Free and open source \u2022 macOS and Windows \u2022 Early, and it says so",
  },
  {
    id: "noghtevorood",
    name: "NOGHTE VOROOD",
    tagline: "Farsi tech podcast",
    description: "A Farsi-language interview podcast for Iranian tech professionals, co-hosted with Ali Parvizi. Long-form conversations with founders, engineers and researchers about work, technology and the future, in a language most of that conversation does not happen in. New episodes stopped in 2026, but the archive stays up and free: 23 episodes, every one still worth listening to.",
    icon: FaMicrophone,
    features: ["Farsi", "Long-form interviews", "Archive, not active"],
    stats: [
      {
        value: "23",
        label: "Episodes",
        icon: FaMicrophone,
      },
      {
        value: "2025",
        label: "Started",
        icon: FaCalendarAlt,
      },
    ],
    cta: {
      primary: {
        text: "Listen to the archive",
        url: "https://noghtevorood.gazar.dev",
        icon: FaExternalLinkAlt,
      },
      secondary: {
        text: "Watch on YouTube",
        url: "https://www.youtube.com/@noghte_vorood",
        icon: FaYoutube,
      },
    },
    disclaimer: "Free \u2022 In Farsi \u2022 Archived, no new episodes",
  },
];

const BusinessShowcase = () => {
  return (
    <div className="w-full bg-white border-y border-border-subtle py-20">
      <ContentColumn anchor="section-businessshowcase" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Header Section */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <Emoji name="man-full-body-dancing-hands-up" size="large" alt="Ventures" />
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-navy tracking-[-0.02em]">
                Things I run
              </h2>
              <div className="h-1 w-20 bg-brand-red mt-4"></div>
            </div>
          </div>
          <p className="font-body text-lg text-ink-soft max-w-3xl leading-relaxed">
            Four things I own and run outside the day job. Three are live and still maintained, and
            each of those is something I use myself. The fourth is a podcast that stopped, kept here
            because the archive is still worth your time and because a list of only the wins would
            be a dishonest one.
          </p>
        </div>

        {/* Business Cards Grid */}
        <div className="space-y-10">
          {BUSINESSES.map((business) => (
            <div key={business.id} className="group">
              <div className="w-full bg-white border-2 border-border-subtle rounded-smooth-lg p-8 md:p-12 transition-colors duration-200 hover:border-border-strong">
                <div className="flex flex-col lg:flex-row gap-10 lg:gap-12 items-start">
                  <div className="flex-1 space-y-6">
                    {/* Header */}
                    <div className="flex items-center gap-4">
                      {business.logo ? (
                        <img
                          src={business.logo}
                          alt=""
                          width={56}
                          height={56}
                          className="w-14 h-14 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-gray-900 text-gray-50 flex items-center justify-center flex-shrink-0">
                          <business.icon className="text-xl" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-2xl font-bold text-gray-900 mb-1 uppercase tracking-[0.08em]">
                          {business.name}
                        </h3>
                        <p className="font-body text-sm text-[#232323]/70 uppercase tracking-[0.2em]">
                          {business.tagline}
                        </p>
                      </div>
                    </div>
                    
                    {/* Description */}
                    <p className="font-body text-[#232323]/80 leading-relaxed text-base">
                      {business.description}
                    </p>

                    {/* Features */}
                    <div className="flex flex-wrap gap-2">
                      {business.features.map((feature, idx) => (
                        <span
                          key={idx}
                          className="bg-gray-900 text-gray-50 px-3 py-1.5 rounded-full text-xs font-heading font-semibold uppercase tracking-wide"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="w-full lg:w-[22rem] space-y-6">
                    {/* Demo (if available). preload="metadata" so the poster
                        paints without pulling the body down the wire for a
                        reader who is scrolling past to the next venture. */}
                    {business.video && (
                      <video
                        className="w-full rounded-smooth-lg border border-gray-900/10 bg-[#0d1016]"
                        controls
                        preload="metadata"
                        playsInline
                        poster={business.video.poster}
                        aria-label={business.video.label}
                      >
                        <source src={business.video.src} type="video/mp4" />
                      </video>
                    )}

                    {/* Stats (if available) */}
                    {business.stats && (
                      <div className="grid grid-cols-2 gap-4">
                        {business.stats.map((stat, idx) => {
                          const StatIcon = stat.icon;
                          return (
                            <div key={idx} className="bg-gray-900/5 border border-gray-900/10 rounded-smooth-lg px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-2 mb-1 text-gray-900">
                                <StatIcon className="text-sm text-gray-900/70" />
                                <div className="text-2xl font-heading font-bold">{stat.value}</div>
                              </div>
                              <div className="text-xs font-body text-gray-900/60 uppercase tracking-[0.18em]">
                                {stat.label}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* CTA Section */}
                    <div className="space-y-3">
                      <a
                        href={business.cta.primary.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center px-5 py-3 bg-gray-900 text-gray-50 font-heading font-bold rounded-smooth-lg hover:bg-gray-900/90 transition-colors duration-200 text-sm uppercase tracking-[0.18em]"
                      >
                        <business.cta.primary.icon className="mr-2" />
                        {business.cta.primary.text}
                      </a>
                      
                      {business.cta.secondary && (
                        <a
                          href={business.cta.secondary.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full inline-flex items-center justify-center px-5 py-2.5 border border-gray-900/40 text-gray-900 font-heading font-bold rounded-smooth-lg hover:bg-gray-900/5 transition-all duration-200 text-sm uppercase tracking-[0.18em]"
                        >
                          <business.cta.secondary.icon className="mr-2" />
                          {business.cta.secondary.text}
                        </a>
                      )}
                    </div>

                    {/* Disclaimer */}
                    {business.disclaimer && (
                      <p className="font-body text-xs text-[#232323]/60 text-center border-t border-gray-900/10 pt-4">
                        {business.disclaimer}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ContentColumn>
    </div>
  );
};

export default BusinessShowcase;
